import { ChartData, ChartOptions } from 'chart.js';
import { SeriesPoint } from './metrics';

export interface LineSeriesSpec {
  label: string;
  color: string;
  dashed?: boolean;
  series: SeriesPoint[];
}

// Aligns every series onto one shared, sorted date axis; missing dates render
// as gaps bridged by spanGaps so coarser series stay continuous.
export const buildLineChartData = (specs: LineSeriesSpec[]): ChartData<'line'> => {
  const dateSet = new Set<string>();
  specs.forEach(spec => spec.series.forEach(point => dateSet.add(point.date)));
  const labels = [...dateSet].sort();

  return {
    labels,
    datasets: specs.map(spec => {
      const byDate = new Map(spec.series.map(point => [point.date, point.value]));
      return {
        label: spec.label,
        data: labels.map(date => byDate.get(date) ?? null),
        borderColor: spec.color,
        backgroundColor: spec.color,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 3,
        spanGaps: true,
        ...(spec.dashed ? { borderDash: [6, 4] } : {}),
      };
    }),
  };
};

// nearestTooltip shows only the hovered line instead of every series at the
// date — needed when there are too many series for an index-mode list.
export const buildLineChartOptions = (
  formatValue: (value: number) => string,
  { nearestTooltip = false } = {}
): ChartOptions<'line'> => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  interaction: nearestTooltip ? { mode: 'nearest', intersect: false } : { mode: 'index', intersect: false },
  plugins: {
    legend: { position: 'bottom', labels: { boxWidth: 12, usePointStyle: true } },
    tooltip: {
      backgroundColor: '#FFFFFF',
      titleColor: '#1F2937',
      titleFont: { weight: 'bold', size: 13 },
      bodyColor: '#4B5563',
      borderColor: 'rgba(0, 0, 0, 0.08)',
      borderWidth: 1,
      cornerRadius: 8,
      padding: 10,
      callbacks: {
        label: context => `${context.dataset.label}: ${formatValue(context.parsed.y)}`,
      },
    },
  },
  scales: {
    x: {
      border: { display: false, dash: [4, 4] },
      grid: { color: '#EEF1F5' },
      ticks: {
        color: '#9CA3AF',
        maxTicksLimit: 8,
        maxRotation: 0,
        callback(value) {
          const label = this.getLabelForValue(Number(value));
          const date = new Date(`${label}T00:00:00`);
          if (Number.isNaN(date.getTime())) {
            return label;
          }
          return [date.toLocaleString('en-US', { month: 'short' }).toUpperCase(), String(date.getFullYear())];
        },
      },
    },
    y: {
      border: { display: false, dash: [4, 4] },
      grid: { color: '#E5E7EB' },
      ticks: { color: '#9CA3AF', callback: value => formatValue(Number(value)) },
    },
  },
});
